<template>
  <div class="mini-registration-form">
    <h2 class="main-title">Startmomenten</h2>
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">Cursussen worden geladen...</p>
    </div>
    <div v-else-if="hasError" class="error-container">
      <p class="error-message">{{ errorMessage }}</p>
      <button class="retry-button" @click="fetchCategories">Opnieuw proberen</button>
    </div>
    <div v-else class="mini-registration-layout">
      <aside class="filters">
        <div class="filter-block">
          <div class="filter-header" @click="toggleFilter('course')">
            <h3>Kies jouw cursus</h3>
            <span class="toggle-icon">
              <svg v-if="expandedFilters.course" xmlns="http://www.w3.org/2000/svg" width="15" height="13" viewBox="0 0 17.357 12.594">
                <path id="Path_451_open_course" data-name="Path 451" d="M-20627.77-22967.5l7.172,8.234,7.17-8.234" transform="translate(-20611.92 -22956.221) rotate(180)" fill="none" stroke="#000" stroke-width="4"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="15" height="13" viewBox="0 0 17.357 12.594">
                <path id="Path_451_closed_course" data-name="Path 451" d="M-20627.77-22967.5l7.172,8.234,7.17-8.234" transform="translate(20629.277 22968.814)" fill="none" stroke="#000" stroke-width="4"/>
              </svg>
            </span> 
          </div>
          <div class="filter-content" v-show="expandedFilters.course">
            <select id="course-select" v-model="selectedCategoryId" @change="onCategoryChange">
              <option value="" disabled>Kies een cursus</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
            <select v-model="selectedCourseType" :disabled="!selectedCategoryId || courseTypes.length === 0">
              <option value="" disabled>Kies een type</option>
              <option v-for="type in courseTypes" :key="type" :value="type">{{ type }}</option>
            </select>
            <select v-model="selectedLanguage" :disabled="!selectedCategoryId || languages.length === 0">
              <option value="" disabled>Kies een taal</option>
              <option v-for="lang in languages" :key="lang" :value="lang">{{ lang }}</option>
            </select>
            <select
              v-model="selectedSpecificCourseId"
              :disabled="!selectedCourseType || !selectedLanguage"
            >
              <option value="" disabled>{{ !selectedCourseType || !selectedLanguage ? 'Kies eerst type en taal' : 'Kies een specifieke cursus' }}</option>
              <option v-for="course in filteredSpecificCourses" :key="course.id" :value="course.id">
                {{ course.name }}
              </option>
            </select>
            <button type="button" class="clear-selection-btn" @click="clearCourseSelection">
              Wis selectie
            </button>
          </div>
        </div>
        <div class="filter-block">
          <div class="filter-header" @click="toggleFilter('participants')">
            <h3>Aantal cursisten</h3>
            <span class="toggle-icon">
              <svg v-if="expandedFilters.participants" xmlns="http://www.w3.org/2000/svg" width="15" height="12.594" viewBox="0 0 17.357 12.594">
                <path id="Path_451_open_participants" data-name="Path 451" d="M-20627.77-22967.5l7.172,8.234,7.17-8.234" transform="translate(-20611.92 -22956.221) rotate(180)" fill="none" stroke="#000" stroke-width="4"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="15" height="12.594" viewBox="0 0 17.357 12.594">
                <path id="Path_451_closed_participants" data-name="Path 451" d="M-20627.77-22967.5l7.172,8.234,7.17-8.234" transform="translate(20629.277 22968.814)" fill="none" stroke="#000" stroke-width="4"/>
              </svg>
            </span>
          </div>
          <div class="filter-content" v-show="expandedFilters.participants">
            <select
              id="participant-count"
              v-model.number="participantCount"
              class="participant-count-input"
            >
              <option v-for="count in 12" :key="count" :value="count">{{ count }}</option>
            </select>
          </div>
        </div>
        <div class="filter-block">
          <div class="filter-header" @click="toggleFilter('location')">
            <h3>Kies de locatie</h3>
            <span class="toggle-icon">
              <svg v-if="expandedFilters.location" xmlns="http://www.w3.org/2000/svg" width="15" height="12.594" viewBox="0 0 17.357 12.594">
                <path id="Path_451_open_location" data-name="Path 451" d="M-20627.77-22967.5l7.172,8.234,7.17-8.234" transform="translate(-20611.92 -22956.221) rotate(180)" fill="none" stroke="#000" stroke-width="4"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="15" height="12.594" viewBox="0 0 17.357 12.594">
                <path id="Path_451_closed_location" data-name="Path 451" d="M-20627.77-22967.5l7.172,8.234,7.17-8.234" transform="translate(20629.277 22968.814)" fill="none" stroke="#000" stroke-width="4"/>
              </svg>
            </span>
          </div>
          <div class="filter-content" v-show="expandedFilters.location">
            <div class="location-search-container">
              <input 
                id="location-input" 
                type="text" 
                placeholder="Postcode of plaats" 
                v-model="locationSearchQuery"
                @input="onLocationSearchInput"
                @blur="onLocationSearchBlur"
              />
            </div>
            
            <select id="radius-select" v-model="selectedRadius">
                <option value="10">&lt; 10 km</option>
                <option value="20">&lt; 20 km</option>
                <option value="40">&lt; 40 km</option>
                <option value="50">&lt; 50 km</option>
                <option value="100">&lt; 100 km</option>
            </select>

            <div class="geocoding-feedback-container" :style="{ minHeight: locationSearchQuery.trim() ? '58px' : '0px' }">
              <div v-if="isGeocoding" class="geocoding-indicator">
                <div class="geocoding-spinner"></div>
                <span class="geocoding-text">Zoeken...</span>
              </div>
              <div v-if="geocodingError" class="geocoding-error">
                {{ geocodingError }}
              </div>
              <div v-if="geocodingResult" class="geocoding-result">
                <span class="found-address">{{ geocodingResult.formattedAddress }}</span>
              </div>
            </div>
            
            <div v-if="availableLocations.length > 0" class="location-results">
              <h4 v-if="locationSearchQuery" class="location-results-title">Beschikbare locaties binnen {{ selectedRadius }} km:</h4>
              <h4 v-else class="location-results-title">Alle beschikbare locaties:</h4>
              <div class="location-checkboxes">
                <label 
                  v-for="location in availableLocations" 
                  :key="location.id"
                >
                  <input 
                    type="checkbox" 
                    :value="location.id"
                    v-model="selectedLocationIds"
                    @change="onLocationSelectionChange"
                  /> 
                  <span class="location-name">
                    {{ getCityName(location.name) }}<span v-if="location.distance > 0" class="location-distance">({{ formatDistance(location.distance) }})</span>
                  </span>
                </label>
              </div>
            </div>
            
            <div v-else-if="locationSearchQuery && !isGeocoding && !geocodingError" class="no-locations">
              <p class="no-locations-text">Geen locaties gevonden binnen {{ selectedRadius }} km van "{{ locationSearchQuery }}"</p>
            </div>
            
            <div v-else-if="!locationSearchQuery && availableLocations.length === 0" class="no-locations">
              <p class="no-locations-text">Locaties worden geladen...</p>
            </div>
          </div>
        </div>
        <div class="filter-block">
          <div class="filter-header" @click="toggleFilter('months')">
            <h3>Kies de startmaand</h3>
            <span class="toggle-icon">
              <svg v-if="expandedFilters.months" xmlns="http://www.w3.org/2000/svg" width="15" height="12.594" viewBox="0 0 17.357 12.594">
                <path id="Path_451_open_months" data-name="Path 451" d="M-20627.77-22967.5l7.172,8.234,7.17-8.234" transform="translate(-20611.92 -22956.221) rotate(180)" fill="none" stroke="#000" stroke-width="4"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="15" height="12.594" viewBox="0 0 17.357 12.594">
                <path id="Path_451_closed_months" data-name="Path 451" d="M-20627.77-22967.5l7.172,8.234,7.17-8.234" transform="translate(20629.277 22968.814)" fill="none" stroke="#000" stroke-width="4"/>
              </svg>
            </span>
          </div>
          <div class="filter-content" v-show="expandedFilters.months">
            <div class="month-checkboxes">
              <label v-for="month in availableMonths" :key="month.value">
                <input 
                  type="checkbox" 
                  :value="month.value"
                  v-model="selectedMonths"
                  @change="onMonthFilterChange"
                /> 
                {{ month.label }}
              </label>
            </div>
          </div>
        </div>
      </aside>
      <section class="moments-table-section">
        <MiniCourseMoments
          :moments="paginatedMoments"
          :isLoading="isLoadingMoments"
          :hasError="hasErrorMoments"
          :errorMessage="errorMessageMoments"
          @retry="fetchMoments"
          @inschrijven="handleInschrijven"
        />
        <div class="pagination-controls" v-if="totalPages > 1">
          <button @click="prevPage" :disabled="currentPage === 1">Vorige</button>
          <span class="pagination-info">Pagina {{ currentPage }} van {{ totalPages }}</span>
          <button @click="nextPage" :disabled="currentPage === totalPages">Volgende</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { geocodeAddress } from '@/utils/locationUtils'
import { API_DOMAIN } from '@/config'
import MiniCourseMoments from '@/components/mini-registration/MiniCourseMoments.vue'
import type { Coordinates } from '@/types'

// Dynamische locaties die worden opgehaald van de REST API
const availableLocationsData = ref<any[]>([])
import type { Category, Course, LocationWithDistance, GeocodingResult } from '@/types'
import { 
  calculateDistancesForLocations, 
  filterLocationsByDistance, 
  formatDistance
} from '@/utils/locationUtils'

// Functie om alleen de stadnaam te extraheren uit het volledige adres
const getCityName = (fullLocationName: string) => {
  const parts = fullLocationName.split(',').map(part => part.trim())
  
  // Bekende stadnamen die we willen tonen
  const knownCities = [
    'Amsterdam', 'Ede', 'Apeldoorn', 'Arnhem', 'Breda', 'Den Haag', 'Best', 'Emmen', 
    'Ten Boer', 'Hardinxveld-Giessendam', 'Hengelo', 'Lelystad', 'Maastricht', 'Nijkerk', 
    'Wijchen', 'Capelle aan den IJssel', 'Houten', 'Venlo', 'Venray', 'Waddinxveen', 
    'Wolvega', 'Zoetermeer', 'Alkmaar', 'Eindhoven', 'Rotterdam', 'Zwolle'
  ]
  
  // Speciale mapping voor locaties die verschillende namen kunnen hebben
  const locationMappings: { [key: string]: string } = {
    'Eindhoven (Best)': 'Best',
    'Rotterdam (Capelle)': 'Capelle aan den IJssel',
    'Capelle aan den IJssel': 'Capelle aan den IJssel',
    'Utrecht (Houten)': 'Houten',
    'Groningen (Ten Boer)': 'Ten Boer',
    'Den Haag': 'Den Haag',
    "'s-Gravenhage": 'Den Haag',
    's-Gravenhage': 'Den Haag'
  }
  
  // Check eerst voor speciale mappings
  if (locationMappings[fullLocationName]) {
    return locationMappings[fullLocationName]
  }
  
  // Check voor patronen zoals "Eindhoven (Best)"
  for (const [pattern, city] of Object.entries(locationMappings)) {
    if (fullLocationName.includes(pattern.split(' (')[0]) && fullLocationName.includes('(')) {
      return city
    }
  }
  
  // Zoek naar een bekende stadnaam
  for (const part of parts) {
    if (knownCities.includes(part)) {
      return part
    }
  }
  
  // Als geen bekende stad gevonden, zoek naar het deel dat geen postcode, provincie of "Nederland" is
  // en niet begint met een cijfer (straatnaam)
  for (const part of parts) {
    // Skip postcodes (bijv. "1822 BX", "7317 AW")
    if (/\d{4}\s?[A-Z]{2}/.test(part)) continue
    
    // Skip provincies
    if (part === 'Gelderland' || part === 'Noord-Holland' || part === 'Zuid-Holland' || 
        part === 'Noord-Brabant' || part === 'Limburg' || part === 'Utrecht' || 
        part === 'Flevoland' || part === 'Overijssel' || part === 'Drenthe' || 
        part === 'Groningen' || part === 'Fryslân' || part === 'Zeeland') continue
    
    // Skip "Nederland"
    if (part === 'Nederland') continue
    
    // Skip straatnamen die beginnen met cijfers (bijv. "93D", "24", "30")
    if (/^\d/.test(part)) continue
    
    // Skip bekende straatnamen/gebieden
    if (part.includes('weg') || part.includes('straat') || part.includes('laan') || 
        part.includes('park') || part.includes('singel') || part.includes('kade') ||
        part.includes('Otterkoog') || part.includes('Hemweg') || part.includes('Stadhoudersmolenweg') ||
        part.includes('Overmaat') || part.includes('Beverkoog') || part.includes('Hempoint') ||
        part.includes('Westpoort') || part.includes('Stadhoudersmolen') || part.includes('Elden') ||
        part.includes('Breeven') || part.includes('Bargermeer') || part.includes('Ten Boer') ||
        part.includes('Wielwijk') || part.includes('Twentekanaal') || part.includes('Oostervaart') ||
        part.includes('Malberg') || part.includes('Bronswerk') || part.includes('Industriegebied') ||
        part.includes('Schollevaar') || part.includes('Doornkade') || part.includes('Oud-Wulven') ||
        part.includes('Heierhoeve') || part.includes('Trade Port') || part.includes('Blerick') ||
        part.includes('Smakterheide') || part.includes('West 2') || part.includes('Schipsloot') ||
        part.includes('Lansinghage')) continue
    
    // Dit is waarschijnlijk de stadnaam
    return part
  }
  
  // Fallback: neem het eerste deel als er niets anders gevonden wordt
  return parts[0] || fullLocationName
}

// Breid Course lokaal uit voor deze component
interface MiniCourse extends Course {
  course_types?: string[]
  language?: string
}

const categories = ref<Category[]>([])
const isLoading = ref(true)
const hasError = ref(false)
const errorMessage = ref('')
// Get shortcode parameters from data attributes
const getShortcodeParams = (): {
  categorie: string
  cursus: string
  type: string
  taal: string
  locaties: string
  personen: string
} => {
  const element = document.getElementById('mq-vue-mini-registration')
  console.log('🔍 MiniRegistrationForm: Looking for element with id "mq-vue-mini-registration"')
  console.log('🔍 MiniRegistrationForm: Element found:', !!element)
  
  if (!element) {
    console.log('🔍 MiniRegistrationForm: No element found, returning empty params')
    return {
      categorie: '',
      cursus: '',
      type: '',
      taal: '',
      locaties: '',
      personen: ''
    }
  }
  
  const params = {
    categorie: element.dataset.categorie || '',
    cursus: element.dataset.cursus || '',
    type: element.dataset.type || '',
    taal: element.dataset.taal || '',
    locaties: element.dataset.locaties || '',
    personen: element.dataset.personen || ''
  }
  
  console.log('🔍 MiniRegistrationForm: Shortcode parameters:', params)
  return params
}

const shortcodeParams = getShortcodeParams()

const selectedCategoryId = ref<string>(shortcodeParams.categorie || '')

// Nieuw: specifieke cursussen
const specificCourses = ref<MiniCourse[]>([])
const isLoadingSpecific = ref(false)
const selectedSpecificCourseId = ref<string>(shortcodeParams.cursus ?? '')
const courseTypes = ref<string[]>([])
const selectedCourseType = ref<string>(shortcodeParams.type ?? '')
const languages = ref<string[]>([])
const selectedLanguage = ref<string>(shortcodeParams.taal ?? '')
const participantCount = ref<number>(parseInt(shortcodeParams.personen || '1'))

// Maandfilter state
const selectedMonths = ref<string[]>([])

// Locatie-gebaseerde filtering state
const locationSearchQuery = ref<string>('')
const selectedRadius = ref<number>(40)
const isGeocoding = ref<boolean>(false)
const geocodingError = ref<string>('')
const geocodingResult = ref<GeocodingResult | null>(null)
const availableLocations = ref<LocationWithDistance[]>([])
const selectedLocationIds = ref<string[]>([])

// Debounce timer voor geocoding
let geocodingTimeout: number | null = null

// Helper functie om maand uit datum te halen (YYYY-MM format)
const getMonthFromDate = (dateString: string): string => {
  if (!dateString) return ''
  // Zorg ervoor dat we de datum correct parsen ongeacht het formaat
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

// Beschikbare maanden genereren (volgende 12 maanden)
const availableMonths = computed(() => {
  const months = []
  const currentDate = new Date()
  
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1)
    const monthName = date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })
    // Gebruik dezelfde logica als getMonthFromDate voor consistentie
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const monthValue = `${year}-${month}`
    months.push({
      label: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      value: monthValue
    })
  }
  
  return months
})

const filteredSpecificCourses = computed(() => {
  if (!selectedCourseType.value || !selectedLanguage.value) return []
  return specificCourses.value.filter(c => {
    const typeMatch = c.course_types?.includes(selectedCourseType.value)
    const langMatch = c.language === selectedLanguage.value
    return typeMatch && langMatch
  })
})

// Watch for changes in course type and language
watch([selectedCourseType, selectedLanguage], () => {
  // Only reset if we don't have shortcode parameters
  const hasShortcodeParams = shortcodeParams.categorie || shortcodeParams.cursus || shortcodeParams.type || shortcodeParams.taal || shortcodeParams.personen
  
  if (!hasShortcodeParams) {
    selectedSpecificCourseId.value = ''
    
    // Auto-select first available specific course when type and language are selected
    if (selectedCourseType.value && selectedLanguage.value) {
      const availableCourses = filteredSpecificCourses.value
      if (availableCourses.length > 0) {
        selectedSpecificCourseId.value = availableCourses[0].id
      }
    }
  } else {
    // With shortcode parameters, check if current selection is still valid
    if (selectedCourseType.value && selectedLanguage.value) {
      const availableCourses = filteredSpecificCourses.value
      const currentCourse = specificCourses.value.find(c => c.id === selectedSpecificCourseId.value)
      
      // If current course is not valid anymore, select first available
      if (!currentCourse || !availableCourses.find(c => c.id === selectedSpecificCourseId.value)) {
        if (availableCourses.length > 0) {
          selectedSpecificCourseId.value = availableCourses[0].id
        }
      }
    }
  }
})

const selectedSpecificCourseName = computed(() => {
  if (!selectedSpecificCourseId.value) return ''
  const course = specificCourses.value.find(c => c.id == selectedSpecificCourseId.value) // Use == for type coercion
  console.log('🔍 selectedSpecificCourseName computed - selectedSpecificCourseId:', selectedSpecificCourseId.value)
  console.log('🔍 selectedSpecificCourseName computed - specificCourses length:', specificCourses.value.length)
  console.log('🔍 selectedSpecificCourseName computed - found course:', course)
  return course?.name || ''
})

// Auto-fill logic when specific course is selected
watch(selectedSpecificCourseId, (newId) => {
  if (newId) {
    const course = specificCourses.value.find(c => c.id == newId) // Use == for type coercion
    if (course) {
      // Auto-fill course type if not already set
      if (!selectedCourseType.value && course.course_types && course.course_types.length > 0) {
        selectedCourseType.value = course.course_types[0]
      }
      
      // Auto-fill language if not already set
      if (!selectedLanguage.value && course.language) {
        selectedLanguage.value = course.language
      }
    }
  }
})

// State voor de startmomenten
const moments = ref<any[]>([])
const isLoadingMoments = ref(false)
const hasErrorMoments = ref(false)
const errorMessageMoments = ref('')

// Paginatie state
const currentPage = ref(1)
const itemsPerPage = ref(10)

const sortedMoments = computed(() => {
  let filteredMoments = [...moments.value]
  
  // Filter op geselecteerde maanden
  if (selectedMonths.value.length > 0) {
    filteredMoments = filteredMoments.filter((moment: any) => {
      if (!moment.start_date) return false
      const momentMonth = getMonthFromDate(moment.start_date)
      return selectedMonths.value.includes(momentMonth)
    })
  }
  
  // Filter op geselecteerde locaties
  if (selectedLocationIds.value.length > 0) {
    const selectedLocationNames = availableLocations.value
      .filter(loc => selectedLocationIds.value.includes(loc.id))
      .map(loc => getCityName(loc.name)) // Gebruik de verkorte stadnaam
    
    console.log('MiniForm: Selected location names for filtering:', selectedLocationNames)
    
    filteredMoments = filteredMoments.filter((moment: any) => {
      const momentLocation = moment.city || moment.location || ''
      const momentCityName = getCityName(momentLocation) // Converteer moment locatie naar stadnaam
      
      console.log('MiniForm: Checking moment:', momentCityName, 'against selected:', selectedLocationNames)
      
      return selectedLocationNames.includes(momentCityName)
    })
    
    console.log('MiniForm: Filtered moments count:', filteredMoments.length)
  }
  
  // Sorteer op datum en locatie
  return filteredMoments.sort((a: any, b: any) => {
    const dateA = a.start_date || ''
    const dateB = b.start_date || ''
    
    const dateComparison = dateA.localeCompare(dateB)
    if (dateComparison !== 0) {
      return dateComparison
    }

    // Als datums gelijk zijn, sorteer op locatie
    const locationA = (a.city || a.location || '').toLowerCase()
    const locationB = (b.city || b.location || '').toLowerCase()
    return locationA.localeCompare(locationB)
  })
})

const totalPages = computed(() => {
  return Math.ceil(sortedMoments.value.length / itemsPerPage.value)
})

const paginatedMoments = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return sortedMoments.value.slice(start, end)
})

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

// Locatie-gebaseerde filtering functies
const onLocationSearchInput = () => {
  // Clear previous results
  geocodingError.value = ''
  geocodingResult.value = null
  availableLocations.value = []
  selectedLocationIds.value = []
  
  // Clear previous timeout
  if (geocodingTimeout) {
    clearTimeout(geocodingTimeout)
  }
  
  // Debounce geocoding request
  if (locationSearchQuery.value.trim()) {
    geocodingTimeout = setTimeout(() => {
      performGeocoding()
    }, 500)
  }
}

const onLocationSearchBlur = () => {
  // Perform geocoding immediately on blur if there's a query
  if (locationSearchQuery.value.trim() && !geocodingResult.value) {
    if (geocodingTimeout) {
      clearTimeout(geocodingTimeout)
    }
    performGeocoding()
  }
}

const performGeocoding = async () => {
  if (!locationSearchQuery.value.trim()) return
  
  isGeocoding.value = true
  geocodingError.value = ''
  
  try {
    const result = await geocodeAddress(locationSearchQuery.value.trim())
    
    if (result) {
      geocodingResult.value = result
      calculateAvailableLocations(result.coordinates)
    } else {
      geocodingError.value = 'Locatie niet gevonden. Probeer een andere postcode of plaatsnaam.'
    }
  } catch (error: any) {
    geocodingError.value = 'Er is een fout opgetreden bij het zoeken naar de locatie.'
    console.error('Geocoding error:', error)
  } finally {
    isGeocoding.value = false
  }
}

const fetchLocations = async () => {
  try {
    console.log('MiniForm: Fetching locations from REST API')
    const response = await fetch(`${API_DOMAIN}/wp-json/wc/v3/locations/`, {
      headers: { 'Accept': 'application/json' }
    })
    
    console.log('MiniForm: API response status:', response.status)
    
    if (!response.ok) {
      throw new Error(`Locations API request failed with status ${response.status}`)
    }
    
    const data = await response.json()
    console.log('MiniForm: Fetched locations raw data:', data)
    console.log('MiniForm: Data type:', typeof data)
    console.log('MiniForm: Is array:', Array.isArray(data))
    
    if (Array.isArray(data)) {
      availableLocationsData.value = data
      console.log('MiniForm: Set availableLocationsData to:', availableLocationsData.value)
      
      // DIRECT ALLE LOCATIES TONEN ZONDER GEOCODING
      // Converteer naar het juiste formaat voor LocationWithDistance
      availableLocations.value = data.map(location => ({
        id: location.id,
        name: location.name,
        distance: 0, // Geen afstand berekening nodig
        coordinates: location.coordinates
      }))
      
      console.log('MiniForm: Set availableLocations to:', availableLocations.value)
      
      // Pre-select location from shortcode if provided
      if (shortcodeParams.locaties && shortcodeParams.locaties !== 'Alle locaties') {
        console.log('MiniForm: Checking for shortcode location:', shortcodeParams.locaties)
        
        // Extract city name from shortcode parameter (handles both full and city-only names)
        const shortcodeLocationName = getCityName(shortcodeParams.locaties)
        console.log('MiniForm: Extracted city name:', shortcodeLocationName)
        
        // Find matching location by comparing city names
        const matchingLocation = availableLocations.value.find(loc => {
          const locationCityName = getCityName(loc.name)
          console.log('MiniForm: Comparing', locationCityName, 'with', shortcodeLocationName)
          return locationCityName === shortcodeLocationName
        })
        
        if (matchingLocation) {
          console.log('MiniForm: Found matching location, pre-selecting:', matchingLocation.id)
          selectedLocationIds.value = [matchingLocation.id]
        } else {
          console.log('MiniForm: No matching location found for:', shortcodeLocationName)
        }
      }
    } else {
      console.error('MiniForm: Invalid locations data format:', data)
      availableLocationsData.value = []
    }
  } catch (error: any) {
    console.error('MiniForm: Error fetching locations:', error)
    // Fallback naar hardcoded locaties als de API faalt
    availableLocationsData.value = [
      {
        id: 'ede-gelderland-nederland',
        name: 'Ede, Gelderland, Nederland',
        coordinates: {
          latitude: 52.0333,
          longitude: 5.6667
        }
      },
      {
        id: 'amsterdam-noord-holland-nederland',
        name: 'Amsterdam, Noord-Holland, Nederland',
        coordinates: {
          latitude: 52.3676,
          longitude: 4.9041
        }
      }
    ]
    
    // Ook fallback locaties direct tonen
    availableLocations.value = availableLocationsData.value.map(location => ({
      id: location.id,
      name: location.name,
      distance: 0,
      coordinates: location.coordinates
    }))
    
    console.log('MiniForm: Using fallback locations:', availableLocations.value)
    
    // Pre-select location from shortcode if provided (also for fallback)
    if (shortcodeParams.locaties && shortcodeParams.locaties !== 'Alle locaties') {
      console.log('MiniForm: Checking for shortcode location (fallback):', shortcodeParams.locaties)
      
      // Extract city name from shortcode parameter (handles both full and city-only names)
      const shortcodeLocationName = getCityName(shortcodeParams.locaties)
      console.log('MiniForm: Extracted city name (fallback):', shortcodeLocationName)
      
      // Find matching location by comparing city names
      const matchingLocation = availableLocations.value.find(loc => {
        const locationCityName = getCityName(loc.name)
        console.log('MiniForm: Comparing', locationCityName, 'with', shortcodeLocationName)
        return locationCityName === shortcodeLocationName
      })
      
      if (matchingLocation) {
        console.log('MiniForm: Found matching location, pre-selecting:', matchingLocation.id)
        selectedLocationIds.value = [matchingLocation.id]
      } else {
        console.log('MiniForm: No matching location found for:', shortcodeLocationName)
      }
    }
  }
}

const calculateAvailableLocations = (centerCoordinates: Coordinates) => {
  // Bereken afstanden voor alle opgehaalde locaties
  const locationsWithDistances = calculateDistancesForLocations(
    centerCoordinates,
    availableLocationsData.value
  )
  
  // Filter op basis van geselecteerde straal
  availableLocations.value = filterLocationsByDistance(
    locationsWithDistances,
    selectedRadius.value
  )
}

const onLocationSelectionChange = () => {
  // Reset paginatie wanneer locatie filter verandert
  currentPage.value = 1
}

// Watch voor radius veranderingen
watch(selectedRadius, (newRadius) => {
  if (geocodingResult.value) {
    calculateAvailableLocations(geocodingResult.value.coordinates)
  }
})

const fetchCategories = async () => {
  console.log('MiniForm: Fetching categories')
  
  isLoading.value = true
  hasError.value = false
  errorMessage.value = ''
  
  try {
    const response = await fetch(`${API_DOMAIN}/wp-json/wc/v3/sf_templates`, {
      headers: { 'Accept': 'application/json' }
    })
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`)
    }
    
    const data = await response.json()
    
    if (!Array.isArray(data)) {
      throw new Error('Ongeldige response van de server')
    }
    
    categories.value = data.map((category: Category) => ({
      id: category.id,
      name: category.name
    }))
    
    console.log('MiniForm: Fetched categories:', categories.value.length)
    
  } catch (error: any) {
    console.error('MiniForm: Error fetching categories:', error)
    hasError.value = true
    errorMessage.value = error.message || 'Er is een fout opgetreden bij het ophalen van de cursussen.'
  } finally {
    isLoading.value = false
  }
}

const fetchSpecificCourses = async (categoryId: string): Promise<void> => {
  console.log('MiniForm: Fetching specific courses for category:', categoryId)
  
  isLoadingSpecific.value = true
  specificCourses.value = []
  selectedSpecificCourseId.value = ''
  courseTypes.value = []
  selectedCourseType.value = ''
  languages.value = []
  selectedLanguage.value = ''
  
  try {
    const response = await fetch(`${API_DOMAIN}/wp-json/wc/v3/sf_template?id=${categoryId}`, {
      headers: { 'Accept': 'application/json' }
    })
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`)
    }
    
    const data = await response.json()
    
    if (!Array.isArray(data)) {
      throw new Error('Ongeldige response van de server')
    }
    
    specificCourses.value = data.map((course: any) => ({
      id: course.id,
      name: course.label || course.name,
      price: course.price || 0,
      minAge: course.min_age || 0,
      course_types: course.course_types || [],
      language: course.attributes?.attribute_taal || course.language || 'Nederlands'
    }))
    
    console.log('MiniForm: Mapped specific courses:', specificCourses.value)
    console.log('MiniForm: Raw API data:', data)
    
    // Unieke types en talen verzamelen
    const typeSet = new Set<string>()
    const langSet = new Set<string>()
    data.forEach((item: any) => {
      if (Array.isArray(item.course_types)) {
        item.course_types.forEach((type: string) => typeSet.add(type))
      }
      const taal = item.attributes?.attribute_taal || item.language || 'Nederlands'
      langSet.add(taal)
    })
    courseTypes.value = Array.from(typeSet)
    languages.value = Array.from(langSet)
    
    // Use shortcode parameters if available, otherwise auto-fill first available options
    const hasShortcodeParams = shortcodeParams.categorie || shortcodeParams.cursus || shortcodeParams.type || shortcodeParams.taal || shortcodeParams.personen
    
    if (hasShortcodeParams) {
      // Use shortcode parameters if they exist and are valid
      if (shortcodeParams.type && courseTypes.value.includes(shortcodeParams.type)) {
        selectedCourseType.value = shortcodeParams.type
      } else if (courseTypes.value.length > 0) {
        selectedCourseType.value = courseTypes.value[0]
      }
      
      if (shortcodeParams.taal && languages.value.includes(shortcodeParams.taal)) {
        selectedLanguage.value = shortcodeParams.taal
      } else if (languages.value.length > 0) {
        selectedLanguage.value = languages.value[0]
      }
      
      // For specific course, use shortcode parameter if it exists and is valid
      if (shortcodeParams.cursus) {
        const courseExists = specificCourses.value.find(c => c.id === shortcodeParams.cursus)
        if (courseExists) {
          selectedSpecificCourseId.value = shortcodeParams.cursus
        } else if (courseTypes.value.length === 1 && languages.value.length === 1) {
          // Fallback: auto-select first available course if only one type and language
          const availableCourses = specificCourses.value.filter(c => {
            const typeMatch = c.course_types?.includes(courseTypes.value[0])
            const langMatch = c.language === languages.value[0]
            return typeMatch && langMatch
          })
          if (availableCourses.length > 0) {
            selectedSpecificCourseId.value = availableCourses[0].id
          }
        }
      } else if (courseTypes.value.length === 1 && languages.value.length === 1) {
        // Auto-select first available course if no shortcode parameter
        const availableCourses = specificCourses.value.filter(c => {
          const typeMatch = c.course_types?.includes(courseTypes.value[0])
          const langMatch = c.language === languages.value[0]
          return typeMatch && langMatch
        })
        if (availableCourses.length > 0) {
          selectedSpecificCourseId.value = availableCourses[0].id
        }
      }
    }
    
    console.log('MiniForm: Fetched specific courses:', specificCourses.value.length)
    
  } catch (error: any) {
    console.error('MiniForm: Error fetching specific courses:', error)
    specificCourses.value = []
    courseTypes.value = []
    languages.value = []
  } finally {
    isLoadingSpecific.value = false
  }
}

const onCategoryChange = () => {
  if (selectedCategoryId.value) {
    console.log('MiniForm: Fetching specific courses for category:', selectedCategoryId.value)
    fetchSpecificCourses(selectedCategoryId.value)
  } else {
    console.log('MiniForm: No category selected, clearing specific courses')
    specificCourses.value = []
    selectedSpecificCourseId.value = ''
  }
}

const fetchMoments = async () => {
  console.log('🔍 fetchMoments: Starting...')
  console.log('🔍 selectedSpecificCourseId:', selectedSpecificCourseId.value)
  
  if (!selectedSpecificCourseId.value) {
    console.log('🔍 No specific course selected, returning')
    return
  }

  isLoadingMoments.value = true
  hasErrorMoments.value = false
  errorMessageMoments.value = ''

  try {
    // Gebruik EXACT dezelfde XMLHttpRequest logica als Step3Planning.vue
    console.log('MiniForm: Starting course_moments API call')
    
    // Use XMLHttpRequest - EXACT zoals in Step3Planning.vue
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_DOMAIN}/wp-json/wc/v3/course_moments`, true)
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')
    xhr.setRequestHeader('Accept', 'application/json, text/javascript, */*; q=0.01')
    xhr.withCredentials = true

    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText)
          console.log('MiniForm: API response received')
          processApiResponse(data)
        } catch (e) {
          console.error('MiniForm: Error parsing response:', e)
          hasErrorMoments.value = true
          errorMessageMoments.value = 'Er is een fout opgetreden bij het verwerken van de cursusdata.'
        }
      } else {
        console.error('MiniForm: XHR request failed:', xhr.status, xhr.statusText)
        hasErrorMoments.value = true
        errorMessageMoments.value = `API error: ${xhr.status} ${xhr.statusText}`
      }
      isLoadingMoments.value = false
    }

    xhr.onerror = function () {
      console.error('MiniForm: XHR network error')
      hasErrorMoments.value = true
      errorMessageMoments.value = 'Netwerkfout bij het ophalen van de cursusdata. Controleer uw internetverbinding.'
      isLoadingMoments.value = false
    }

    // Prepare form data - EXACT zoals in Step3Planning.vue
    const formData = new URLSearchParams()

    // Add main course details - EXACT zoals in Step3Planning.vue
    formData.append('product_id', selectedCategoryId.value || selectedSpecificCourseId.value || '63630')
    formData.append('variation_id', selectedSpecificCourseId.value || '63694')
    formData.append('spots', participantCount.value.toString())

    // Add all selected courses (main course and addons) to modules - EXACT zoals in Step3Planning.vue
    console.log('MiniForm: Adding modules - selectedSpecificCourseName:', selectedSpecificCourseName.value)
    console.log('MiniForm: Adding modules - selectedSpecificCourseId:', selectedSpecificCourseId.value)
    if (selectedSpecificCourseName.value && selectedSpecificCourseId.value) {
      formData.append(`modules[${selectedSpecificCourseId.value}]`, selectedSpecificCourseName.value)
      console.log('MiniForm: Added module to form data')
    } else {
      console.log('MiniForm: Skipping module - missing name or ID')
    }

    formData.append('types', selectedCourseType.value?.toLowerCase() || 'basiscursus')
    formData.append('languages[]', selectedLanguage.value || 'Nederlands')

    // Add date range (1 year from now) - EXACT zoals in Step3Planning.vue
    const startDate = new Date()
    const endDate = new Date()
    endDate.setFullYear(endDate.getFullYear() + 1)

    formData.append('start_date', startDate.toISOString().split('T')[0])
    formData.append('end_date', endDate.toISOString().split('T')[0])

    console.log('MiniForm: Sending request with data:', formData.toString())
    console.log('MiniForm: participantCount value:', participantCount.value)
    console.log('MiniForm: participantCount type:', typeof participantCount.value)
    xhr.send(formData.toString())
  } catch (error: any) {
    console.error('MiniForm: Error fetching moments:', error)
    console.error('MiniForm: Error message:', error.message)
    hasErrorMoments.value = true
    errorMessageMoments.value = error.message || 'Er is een fout opgetreden bij het ophalen van de startmomenten.'
    isLoadingMoments.value = false
  }
}

// Process the API response - EXACT zoals in Step3Planning.vue
const processApiResponse = (data: any) => {
  // Check if there are any courses
  if (!data.courses || data.courses.length === 0) {
    console.log('MiniForm: No courses available')
    moments.value = []
    return
  }

  console.log('MiniForm: Processing API response with courses:', data.courses.length)
  console.log('MiniForm: Raw API response structure:', data)
  console.log('MiniForm: First course moment structure:', data.courses[0])
  console.log('MiniForm: First course moment keys:', Object.keys(data.courses[0] || {}))

  // Filter out any invalid course data - EXACT zoals in Step3Planning.vue
  const validCourses = data.courses
    .filter((course: any) =>
      course &&
      course.start_date &&
      course.id &&
      (course.city || course.location)
    )
    .map((course: any) => {
      // Parse course_types to get lunch status - EXACT zoals in Step3Planning.vue
      let lunch = false;
      try {
        const courseTypes = JSON.parse(course.course_types || '[]');
        // Find the matching course type for the selected type
        const matchingType = courseTypes.find((type: any) =>
          type.name.toLowerCase() === selectedCourseType.value.toLowerCase()
        );
        lunch = matchingType?.lunch || false;
      } catch (e) {
        console.error('MiniForm: Error parsing course types:', e);
      }

      return {
        ...course,
        date: new Date(course.start_date),
        lunch: lunch
      };
    });

  moments.value = validCourses
  
  // console.log('MiniForm: Processed moments:', moments.value.length)
  
  // Debug prijsdata voor elk moment
  moments.value.forEach((moment, index) => {
    // console.log(`MiniForm: Moment ${index + 1} price:`, moment.price)
  })
}

// Flag to prevent automatic API calls during shortcode loading
const isShortcodeLoading = ref(false)

watch(selectedSpecificCourseId, (newId) => {
  if (newId && !isShortcodeLoading.value) {
    console.log('MiniForm: Specific course selected, fetching moments')
    currentPage.value = 1 // Reset paginatie
    fetchMoments()
  } else if (!newId) {
    console.log('MiniForm: No specific course selected, clearing moments')
    moments.value = []
  }
})

watch(participantCount, () => {
  if (selectedSpecificCourseId.value) {
    console.log('MiniForm: Participant count changed, fetching moments')
    currentPage.value = 1 // Reset paginatie
    fetchMoments()
  }
})

const handleInschrijven = (moment: any) => {
  console.log('MiniForm: handleInschrijven called with moment:', moment)
  
  // Bereid de data voor om door te geven aan het normale inschrijfformulier
  const params = new URLSearchParams()
  
  // Basis cursus informatie
  params.append('categoryId', selectedCategoryId.value)
  params.append('specificCourseId', selectedSpecificCourseId.value)
  params.append('courseType', selectedCourseType.value)
  params.append('language', selectedLanguage.value)
  params.append('participantCount', participantCount.value.toString())
  params.append('courseName', selectedSpecificCourseName.value)
  
  // Cursusmoment informatie
  params.append('momentId', moment.id)
  params.append('momentDate', moment.start_date)
  params.append('momentTime', moment.start_time || '')
  params.append('momentLocation', moment.city || moment.location || '')
  // Betere prijsafhandeling
  let momentPrice = '0'
  if (moment.price) {
    if (typeof moment.price === 'object' && moment.price.total !== undefined) {
      momentPrice = moment.price.total.toString()
    } else if (typeof moment.price === 'number' || typeof moment.price === 'string') {
      momentPrice = moment.price.toString()
    }
  }
  params.append('momentPrice', momentPrice)
  params.append('momentStartGarantie', moment.start_garantie ? 'true' : 'false')
  
  const url = `/inschrijven?${params.toString()}&step=4`
  console.log('MiniForm: Navigating to:', url)
  
  // Navigeer naar het normale inschrijfformulier met de data
  window.location.href = url
}

const onMonthFilterChange = () => {
  // Reset paginatie wanneer filter verandert
  currentPage.value = 1
}

// Check if we're on mobile (screen width <= 1024px)
const isMobile = () => window.innerWidth <= 1024

const expandedFilters = ref({
  course: !isMobile(),
  participants: !isMobile(),
  location: !isMobile(),
  months: !isMobile()
})

type FilterKey = keyof typeof expandedFilters.value

const toggleFilter = (filter: FilterKey) => {
  expandedFilters.value[filter] = !expandedFilters.value[filter]
}

const clearCourseSelection = () => {
  selectedCategoryId.value = ''
  selectedCourseType.value = ''
  selectedLanguage.value = ''
  selectedSpecificCourseId.value = ''
  
  // Reset gerelateerde data
  specificCourses.value = []
  courseTypes.value = []
  languages.value = []
  moments.value = []
  
  // Reset paginatie
  currentPage.value = 1
}

// Update filter states when window is resized
const updateFilterStates = () => {
  const mobile = isMobile()
  expandedFilters.value = {
    course: !mobile,
    participants: !mobile,
    location: !mobile,
    months: !mobile
  }
}

onMounted(() => {
  console.log('MiniForm: Component mounted')
  
  // Add resize listener to update filter states
  window.addEventListener('resize', updateFilterStates)
  
  // Haal locaties op bij het laden van de component
  fetchLocations()
  
  if (shortcodeParams.categorie) {
    console.log('MiniForm: Loading with shortcode parameters')
    isShortcodeLoading.value = true
    selectedCategoryId.value = shortcodeParams.categorie
    
    if (shortcodeParams.type) {
      selectedCourseType.value = shortcodeParams.type
    }
    if (shortcodeParams.taal) {
      selectedLanguage.value = shortcodeParams.taal
    }
    if (shortcodeParams.personen) {
      participantCount.value = parseInt(shortcodeParams.personen)
    }
    
    fetchCategories().then(() => {
      fetchSpecificCourses(shortcodeParams.categorie).then(() => {
        if (shortcodeParams.cursus) {
          const selectedCourse = specificCourses.value.find(c => c.id == shortcodeParams.cursus)
          if (selectedCourse) {
            selectedSpecificCourseId.value = shortcodeParams.cursus
            setTimeout(() => {
              isShortcodeLoading.value = false
              fetchMoments()
            }, 100)
          } else {
            isShortcodeLoading.value = false
          }
        } else {
          isShortcodeLoading.value = false
        }
      })
    })
  } else {
    fetchCategories()
  }
})

onUnmounted(() => {
  // Remove resize listener when component is unmounted
  window.removeEventListener('resize', updateFilterStates)
})
</script>

<style scoped>
.mini-registration-form {
  background: #fff;
  max-width: 1200px;
  margin: 2rem auto;
  font-family: 'Montserrat', sans-serif;
}

.main-title {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  color: #000;
  margin-bottom: 1.5rem;
}

.loading-text {
  font-family: 'Montserrat', sans-serif;
  color: #666;
}

.error-message {
  font-family: 'Montserrat', sans-serif;
  color: #721c24;
  margin-bottom: 1rem;
}

.retry-button {
  font-family: 'Montserrat', sans-serif;
  background: #0099e3;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.retry-button:hover {
  background: #007bb5;
}
.mini-registration-layout {
  display: flex;
  gap: 2rem;
}
.filters {
  flex: 0 0 320px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Mobile responsive layout */
@media (max-width: 1024px) {
  .mini-registration-layout {
    flex-direction: column;
    gap: 1rem;
  }
  .filters {
    flex: none;
    width: 100%;
  }
  .moments-table-section {
    width: 100%;
  }
}
.filter-block {
  background: #EDEDED;
  border-radius: 5px;
  padding: 1.5rem 1rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.03);
  margin-bottom: 0;
}
.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1.5rem;
}

.filter-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  color: #000;
  font-family: 'Montserrat', sans-serif;
}

.toggle-icon {
  font-weight: normal;
  font-size: 1rem;
  color: #000;
  transition: transform 0.2s ease;
  line-height: 1;
}

.filter-content {
  margin-top: 1rem;
  transition: all 0.3s ease;
}
.location-checkboxes label,
.month-checkboxes label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 400;
  font-size: 14px !important;
  font-family: 'Montserrat', sans-serif !important;
}
.moments-table-section {
  flex: 1 1 0%;
}
label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-family: 'Montserrat', sans-serif;
}
select, input[type="text"] {
  width: 100%;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #ccc;
  margin-bottom: 1.5rem;
  font-family: 'Montserrat', sans-serif !important;
  font-size: 14px !important;
}

select option {
  font-family: 'Montserrat', sans-serif !important;
}
.loading-container, .error-container {
  text-align: center;
  margin: 2rem 0;
}
.loading-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0099e3;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem auto;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.participant-count-input {
  width: 100%;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #ccc;
  margin-bottom: 1.5rem;
  font-family: 'Montserrat', sans-serif;
}
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  font-family: 'Montserrat', sans-serif;
}

.pagination-controls button {
  background: #0099e3;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  font-family: 'Montserrat', sans-serif;
}

.pagination-controls button:hover {
  background: #007bb5;
}

.pagination-controls button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Locatie filtering styles */
.location-search-container {
  position: relative;
}

.geocoding-feedback-container {
  transition: min-height 0.2s ease-out;
  display: flex;
  align-items: stretch;
}

.geocoding-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #666;
  width: 100%;
  font-family: 'Montserrat', sans-serif;
}

.geocoding-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #0099e3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.geocoding-error {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background: #f8d7da;
  color: #721c24;
  border-radius: 4px;
  font-size: 0.9rem;
  width: 100%;
  line-height: 1.4;
  font-family: 'Montserrat', sans-serif;
}

.geocoding-result {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background: #d4edda;
  color: #155724;
  border-radius: 4px;
  font-size: 0.9rem;
  width: 100%;
  line-height: 1.4;
  font-family: 'Montserrat', sans-serif;
}

.found-address {
  font-weight: 500;
}

.location-results {
  margin-top: 1rem;
}

.location-results h4,
.location-results-title {
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  color: #000;
  font-family: 'Montserrat', sans-serif;
}

.location-checkbox-item input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.location-distance {
  color: #666;
  font-size: 1em;
  font-weight: 400;
  padding-left: 0.25rem;
}

.no-locations {
  padding: 0.75rem;
  background: #fff3cd;
  color: #856404;
  border-radius: 4px;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  text-align: center;
  font-family: 'Montserrat', sans-serif;
}

.no-locations p,
.no-locations-text {
  margin: 0;
  font-family: 'Montserrat', sans-serif;
}

.geocoding-text {
  font-family: 'Montserrat', sans-serif;
}

.pagination-info {
  font-family: 'Montserrat', sans-serif;
  color: #000;
}

.clear-selection-btn {
  background: none;
  border: none;
  color: #666;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  transition: text-decoration 0.2s ease;
  margin-top: 0.5rem;
  display: inline-block;
}

.clear-selection-btn:hover,
.clear-selection-btn:focus {
  text-decoration: underline;
  color: #000;
  background-color: transparent;
}
</style> 